import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Plus, Calendar, Flag, Zap, Archive, Settings, 
  ChevronDown, ChevronRight, Sun, Moon, Star, Clock,
  Inbox, CheckCircle2, Circle, Trash2, Hash, ArrowRight,
  X, XCircle, Check, Target, BarChart3
} from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { tasksService } from '../../lib/tasks-service';
import { taskBig3Integration } from '../../lib/task-big3-integration';
import { format, isToday, isTomorrow, isPast, addDays, startOfWeek } from 'date-fns';

// Natural language date parsing
const parseNaturalDate = (text) => {
  const today = new Date();
  const lowerText = text.toLowerCase();
  
  if (lowerText.includes('today')) return format(today, 'yyyy-MM-dd');
  if (lowerText.includes('tomorrow')) return format(addDays(today, 1), 'yyyy-MM-dd');
  if (lowerText.includes('next week')) return format(addDays(startOfWeek(today), 7), 'yyyy-MM-dd');
  if (lowerText.includes('next month')) return format(addDays(today, 30), 'yyyy-MM-dd');
  
  // Try to parse "in X days"
  const inDaysMatch = lowerText.match(/in (\d+) days?/);
  if (inDaysMatch) {
    return format(addDays(today, parseInt(inDaysMatch[1])), 'yyyy-MM-dd');
  }
  
  // Try to parse day names
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  for (let i = 0; i < days.length; i++) {
    if (lowerText.includes(days[i])) {
      const targetDay = i === 0 ? 1 : i + 1; // Monday is 1
      const currentDay = today.getDay() || 7; // Sunday is 7
      const daysUntil = targetDay > currentDay ? targetDay - currentDay : 7 - (currentDay - targetDay);
      return format(addDays(today, daysUntil), 'yyyy-MM-dd');
    }
  }
  
  return null;
};

// Parse task input for smart features
const parseTaskInput = (input) => {
  let title = input;
  let dueDate = null;
  let priority = 'medium';
  let energyLevel = null;
  let tags = [];
  
  // Extract due date
  const dateMatch = input.match(/(today|tomorrow|next week|next month|in \d+ days?|monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
  if (dateMatch) {
    dueDate = parseNaturalDate(dateMatch[0]);
    title = input.replace(dateMatch[0], '').trim();
  }
  
  // Extract energy level with emojis or text
  if (title.includes('🔴') || title.match(/\bhigh energy\b/i)) {
    energyLevel = 'high';
    title = title.replace(/🔴|high energy/gi, '').trim();
  } else if (title.includes('🟡') || title.match(/\bmedium energy\b/i)) {
    energyLevel = 'medium';
    title = title.replace(/🟡|medium energy/gi, '').trim();
  } else if (title.includes('🟢') || title.match(/\blow energy\b/i)) {
    energyLevel = 'low';
    title = title.replace(/🟢|low energy/gi, '').trim();
  }
  
  // Extract priority with !
  if (title.includes('!!!')) {
    priority = 'high';
    title = title.replace(/!!!/g, '').trim();
  } else if (title.includes('!!')) {
    priority = 'medium';
    title = title.replace(/!!/g, '').trim();
  } else if (title.includes('!')) {
    priority = 'low';
    title = title.replace(/!/g, '').trim();
  }
  
  // Extract tags with #
  const tagMatches = title.match(/#(\w+)/g);
  if (tagMatches) {
    tags = tagMatches.map(tag => tag.substring(1));
    tagMatches.forEach(tag => {
      title = title.replace(tag, '').trim();
    });
  }
  
  return { title, dueDate, priority, energyLevel, tags };
};

// Task groups for Things-like organization
const TASK_GROUPS = {
  inbox: { 
    label: 'Inbox', 
    icon: Inbox, 
    color: 'orange',
    filter: (task) => task.status === 'pending' && (
      task.task_type === 'brain_dump' || 
      task.task_type === 'quick_add' || 
      task.task_type === 'reminder'
    ) && task.status !== 'planned_for_big3'
  },
  today: { 
    label: 'Today', 
    icon: Sun, 
    color: 'yellow',
    filter: (task) => task.due_date && isToday(new Date(task.due_date + 'T00:00:00')) && task.status === 'pending' && task.status !== 'planned_for_big3'
  },
  upcoming: { 
    label: 'Upcoming', 
    icon: Calendar, 
    color: 'azure',
    filter: (task) => task.due_date && !isToday(new Date(task.due_date + 'T00:00:00')) && !isPast(new Date(task.due_date + 'T00:00:00')) && task.status === 'pending' && task.status !== 'planned_for_big3'
  },
  anytime: { 
    label: 'Anytime', 
    icon: Star, 
    color: 'violet',
    filter: (task) => !task.due_date && task.status === 'pending' && task.task_type === 'manual' && task.status !== 'planned_for_big3'
  },
  someday: { 
    label: 'Someday', 
    icon: Moon, 
    color: 'gray',
    filter: (task) => task.category === 'someday' && task.status === 'pending' && task.status !== 'planned_for_big3'
  }
};

export default function IntuitiveTasks() {
  const { currentUser } = useFamilyContext();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [quickAddValue, setQuickAddValue] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const quickAddRef = useRef(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Load tasks
  const loadTasks = useCallback(async () => {
    if (!currentUser?.id) return;
    
    try {
      const allTasks = await tasksService.getTasks(currentUser.id, { includeArchived: false });
      setTasks(allTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Auto-focus input on mount
  useEffect(() => {
    if (quickAddRef.current) {
      quickAddRef.current.focus();
    }
  }, []);

  // Auto-archive check
  useEffect(() => {
    if (currentUser?.id) {
      tasksService.runAutoArchive(currentUser.id).then(() => {
        loadTasks();
      });
    }
  }, [currentUser?.id, loadTasks]);

  // Quick add handler
  const handleQuickAdd = async (e) => {
    if (e.key === 'Enter' && quickAddValue.trim()) {
      e.preventDefault();
      await submitTask();
    }
  };

  // Submit task function
  const submitTask = async () => {
    if (!quickAddValue.trim()) return;
    
    const parsed = parseTaskInput(quickAddValue);
    
    try {
      await tasksService.createTask({
        ownerId: currentUser.id,
        title: parsed.title,
        priority: parsed.priority,
        dueDate: parsed.dueDate,
        energyLevel: parsed.energyLevel,
        category: parsed.tags[0] || null,
        taskType: 'manual'
      });
      
      setQuickAddValue('');
      loadTasks();
      
      // Keep focus on input
      if (quickAddRef.current) {
        quickAddRef.current.focus();
      }
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  // Toggle task completion
  const handleToggleComplete = async (taskId) => {
    try {
      // Optimistic update
      setTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, status: task.status === 'completed' ? 'pending' : 'completed' } : task
      ));
      
      await tasksService.toggleTaskComplete(taskId, currentUser.id);
      
      // Reload after delay for smooth animation
      setTimeout(() => {
        loadTasks();
      }, 300);
    } catch (error) {
      console.error('Error toggling task:', error);
      loadTasks(); // Revert on error
    }
  };

  // Inline edit handler
  const handleInlineEdit = async (taskId) => {
    if (editingTaskId === taskId && editValue.trim()) {
      const parsed = parseTaskInput(editValue);
      
      try {
        await tasksService.updateTask(taskId, {
          title: parsed.title,
          priority: parsed.priority,
          dueDate: parsed.dueDate,
          category: parsed.tags[0] || null
        }, currentUser.id);
        
        setEditingTaskId(null);
        setEditValue('');
        loadTasks();
      } catch (error) {
        console.error('Error updating task:', error);
      }
    }
  };

  // Start editing
  const startEditing = (task) => {
    setEditingTaskId(task.id);
    setEditValue(task.title);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingTaskId(null);
    setEditValue('');
  };

  // Delete task
  const handleDeleteTask = async (taskId) => {
    try {
      await tasksService.deleteTask(taskId, currentUser.id);
      loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  // Toggle Big 3 exclusion
  const handleToggleBig3Exclusion = async (taskId, currentStatus) => {
    try {
      await tasksService.updateTask(taskId, {
        big3_ready: currentStatus === false ? null : false
      }, currentUser.id);
      loadTasks();
    } catch (error) {
      console.error('Error updating Big 3 status:', error);
    }
  };

  // Get tasks for a group
  const getTasksForGroup = (groupKey) => {
    return tasks.filter(task => {
      if (groupKey === 'completed') return task.status === 'completed';
      if (!showCompleted && task.status === 'completed') return false;
      return TASK_GROUPS[groupKey]?.filter(task) || false;
    });
  };

  // Auto-expand/collapse sections based on task count
  useEffect(() => {
    if (tasks.length > 0) {
      const newExpandedState = {};
      Object.keys(TASK_GROUPS).forEach(groupKey => {
        const groupTasks = getTasksForGroup(groupKey);
        // Only expand if section has tasks
        newExpandedState[groupKey] = groupTasks.length > 0;
      });
      // Always collapse completed section initially
      newExpandedState.completed = false;
      setExpandedSections(newExpandedState);
    }
  }, [tasks]);

  // Calculate progress stats
  const totalTasks = tasks.filter(task => task.status === 'pending').length;
  const completedToday = tasks.filter(task => 
    task.status === 'completed' && 
    isToday(new Date(task.updated_at || task.created_at))
  ).length;
  const dueTodayTasks = getTasksForGroup('today');
  const progressPercentage = totalTasks > 0 ? Math.round((completedToday / totalTasks) * 100) : 0;

  // Task component
  const TaskItem = ({ task }) => {
    const isEditing = editingTaskId === task.id;
    const isOverdue = task.due_date && isPast(new Date(task.due_date + 'T00:00:00')) && task.status !== 'completed';
    
    return (
      <div className="group flex items-center gap-3 py-3.5 px-5 border-b border-stone-100 last:border-b-0 transition-all duration-150 hover:bg-stone-50">
        {/* Checkbox */}
        <button
          onClick={() => handleToggleComplete(task.id)}
          className={`flex-shrink-0 w-5 h-5 rounded-md border-2 transition-all duration-150 flex items-center justify-center ${
            task.status === 'completed'
              ? 'bg-azure-500 border-azure-500'
              : 'border-stone-300 hover:border-azure-500'
          }`}
        >
          {task.status === 'completed' && (
            <Check className="w-3 h-3 text-white" />
          )}
        </button>

        {/* Task content */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleInlineEdit(task.id);
                if (e.key === 'Escape') cancelEditing();
              }}
              onBlur={() => handleInlineEdit(task.id)}
              className="w-full px-2 py-1 text-sm border border-azure-300 rounded-md focus:outline-none focus:border-azure-500"
              autoFocus
            />
          ) : (
            <div 
              className={`text-[15px] cursor-text leading-snug ${
                task.status === 'completed' ? 'text-stone-400 line-through' : 'text-stone-900'
              }`}
              onClick={() => startEditing(task)}
            >
              {task.title}
            </div>
          )}
          
          {/* Metadata */}
          <div className="flex items-center gap-3 mt-1">
            {task.due_date && (
              <span className={`text-xs font-medium ${
                isOverdue ? 'text-red-600' : 'text-stone-500'
              }`}>
                {isToday(new Date(task.due_date + 'T00:00:00')) ? 'Today' :
                 isTomorrow(new Date(task.due_date + 'T00:00:00')) ? 'Tomorrow' :
                 format(new Date(task.due_date + 'T00:00:00'), 'MMM d')}
              </span>
            )}
            
            {task.priority === 'high' && (
              <Flag className="w-3 h-3 text-red-500" />
            )}
            
            {task.energy_level && (
              <span className="text-xs flex items-center gap-1">
                {task.energy_level === 'low' && '🟢'}
                {task.energy_level === 'medium' && '🟡'} 
                {task.energy_level === 'high' && '🔴'}
              </span>
            )}
            
            {task.category && (
              <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${
                task.category === 'selected_for_big3' 
                  ? 'bg-gradient-to-r from-violet-100 to-violet-200 text-violet-700 border border-violet-300' 
                  : 'bg-azure-100 text-azure-700'
              }`}>
                {task.category === 'selected_for_big3' ? '🎯 Selected for Big3' : task.category}
              </span>
            )}
            
            {task.task_type === 'reminder' && task.source_message?.sender_name && (
              <span className="text-xs text-violet-600 font-medium">from {task.source_message.sender_name}</span>
            )}
            
            {/* Big 3 Exclusion Indicator - using big3_ready field */}
            {task.big3_ready === false && (
              <div className="flex items-center gap-1">
                <XCircle className="w-3 h-3 text-stone-400" />
                <span className="text-xs text-stone-500">Won't suggest for Big 3</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => handleToggleBig3Exclusion(task.id, task.big3_ready)}
            className={`p-1.5 rounded-md transition-colors ${
              task.big3_ready === false 
                ? 'text-orange-500 hover:text-orange-600 hover:bg-orange-50' 
                : 'text-stone-400 hover:text-orange-500 hover:bg-orange-50'
            }`}
            title={task.big3_ready === false ? "Allow for Big 3 suggestions" : "Exclude from Big 3 suggestions"}
          >
            <XCircle className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeleteTask(task.id)}
            className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
            title="Delete task"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azure-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 xl:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-4 lg:space-y-6">
            {/* Quick Add Section */}
            <div className="relative bg-gradient-to-br from-azure-500 to-azure-600 rounded-xl p-6 text-white overflow-hidden shadow-lg">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white bg-opacity-10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white bg-opacity-5 rounded-full translate-y-12 -translate-x-12"></div>
              
              <div className="relative">
                <h3 className="text-lg font-semibold mb-4">Add a task...</h3>
                <div className="relative">
                  <input
                    ref={quickAddRef}
                    type="text"
                    value={quickAddValue}
                    onChange={(e) => setQuickAddValue(e.target.value)}
                    onKeyDown={handleQuickAdd}
                    placeholder="What needs to be done?"
                    className="w-full px-5 py-4 text-base bg-white text-stone-900 rounded-xl border-0 focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-40 shadow-lg transition-all duration-200"
                  />
                  {/* Mobile Submit Button */}
                  {quickAddValue.trim() && (
                    <button
                      onClick={submitTask}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-azure-600 text-white rounded-lg flex items-center justify-center shadow-md hover:bg-azure-700 transition-colors lg:hidden"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <div className="mt-3 text-sm text-azure-100">
                  Try: "Call dentist tomorrow" • "Review proposal #work" • "Buy milk low energy"
                </div>
              </div>
            </div>

            {/* Task Sections */}
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              {Object.entries(TASK_GROUPS).map(([key, group]) => {
                const groupTasks = getTasksForGroup(key);
                const Icon = group.icon;
                const isExpanded = expandedSections[key] !== false;
                
                return (
                  <div key={key}>
                    {/* Section Header */}
                    <button
                      onClick={() => setExpandedSections(prev => ({ ...prev, [key]: !isExpanded }))}
                      className="w-full flex items-center justify-between px-5 py-4 bg-stone-50 hover:bg-stone-100 border-b border-stone-200 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4 text-stone-600" />
                        <span className="font-semibold text-stone-900 text-[15px]">{group.label}</span>
                        <span className="bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full text-xs font-medium min-w-[20px] text-center">
                          {groupTasks.length}
                        </span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform duration-200 ${
                        isExpanded ? 'rotate-180' : ''
                      }`} />
                    </button>

                    {/* Section Content */}
                    {isExpanded && (
                      <div>
                        {groupTasks.length === 0 ? (
                          <div className="px-5 py-8 text-center text-stone-500 text-sm italic">
                            No tasks
                          </div>
                        ) : (
                          groupTasks.map(task => (
                            <TaskItem key={task.id} task={task} />
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Completed Tasks */}
              {showCompleted && (
                <div>
                  <button
                    onClick={() => setExpandedSections(prev => ({ ...prev, completed: !expandedSections.completed }))}
                    className="w-full flex items-center justify-between px-5 py-4 bg-stone-50 hover:bg-stone-100 border-b border-stone-200 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      <span className="font-semibold text-stone-900 text-[15px]">Completed</span>
                      <span className="bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full text-xs font-medium min-w-[20px] text-center">
                        {getTasksForGroup('completed').length}
                      </span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform duration-200 ${
                      expandedSections.completed ? 'rotate-180' : ''
                    }`} />
                  </button>

                  {expandedSections.completed && (
                    <div>
                      {getTasksForGroup('completed').map(task => (
                        <TaskItem key={task.id} task={task} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Show Completed Toggle - In main column */}
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setShowCompleted(!showCompleted)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showCompleted 
                    ? 'bg-stone-200 text-stone-700'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {showCompleted ? 'Hide' : 'Show'} Completed
              </button>
            </div>
          </div>

          {/* Sidebar - Hidden on Mobile */}
          <div className="hidden lg:flex flex-col gap-5">
            {/* Progress Card */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
              <h3 className="text-base font-semibold text-stone-900 mb-4">Today's Progress</h3>
              
              {/* Progress Circle */}
              <div 
                className="w-[70px] h-[70px] rounded-full mx-auto mb-4 flex items-center justify-center relative"
                style={{
                  background: `conic-gradient(#3b82f6 ${progressPercentage * 3.6}deg, #e2e8f0 0deg)`
                }}
              >
                <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                  <span className="text-lg font-bold text-stone-900">{progressPercentage}%</span>
                </div>
              </div>
              
              <div className="text-center text-sm text-stone-600 mb-4">
                {completedToday} of {totalTasks} tasks completed
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-xl font-bold text-stone-900 leading-none">{totalTasks}</div>
                  <div className="text-xs text-stone-600 font-medium mt-0.5 uppercase tracking-wide">Active</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-stone-900 leading-none">{dueTodayTasks.length}</div>
                  <div className="text-xs text-stone-600 font-medium mt-0.5 uppercase tracking-wide">Due Today</div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-stone-200 p-5">
              <h3 className="text-base font-semibold text-stone-900 mb-4">Recent Activity</h3>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 bg-azure-100 rounded-md flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-azure-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-stone-900">Completed task</div>
                    <div className="text-xs text-stone-500">2 hours ago</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 bg-azure-100 rounded-md flex items-center justify-center">
                    <Plus className="w-3.5 h-3.5 text-azure-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-stone-900">Added 3 new tasks</div>
                    <div className="text-xs text-stone-500">Yesterday</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 bg-azure-100 rounded-md flex items-center justify-center">
                    <Target className="w-3.5 h-3.5 text-azure-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-stone-900">Reached daily goal</div>
                    <div className="text-xs text-stone-500">2 days ago</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}